// src/components/ui/cards/PartnersBusinessCard/PartnersBusinessCardShadcn.jsx
import React, { useState } from 'react';
import {
  Building2,
  MapPin,
  Phone,
  UserPlus,
  Send,
  Loader2
} from 'lucide-react';

// shadcn components
import { Card, CardContent } from '../../card';
import { Button } from '../../button';
import { Textarea } from '../../textarea';
import { Avatar, AvatarFallback } from '../../avatar';

import { cn } from '../../../../utils/cn';

const PartnersBusinessCardShadcn = ({
  business,
  onSendRequest,
  isLoading = false,
  isOffline = false,
  className = ''
}) => {
  const [requestNote, setRequestNote] = useState('');
  const [showRequestForm, setShowRequestForm] = useState(false);

  const handleSendRequest = async () => {
    if (onSendRequest && !isOffline) {
      await onSendRequest(business.id, requestNote);
      setRequestNote('');
      setShowRequestForm(false);
    }
  };

  return (
    <Card className={cn(
      "transition-all duration-300 hover:bg-accent/50",
      isOffline && "opacity-50",
      className
    )}>
      <CardContent className="p-6">
        {/* Business Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12">
              <AvatarFallback className="bg-gradient-to-r from-blue-500 to-purple-600 text-white">
                <Building2 className="w-6 h-6" />
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                {business.name}
              </h3>
              <p className="text-muted-foreground text-sm">
                {business.business_type || 'Service Provider'}
              </p>
            </div>
          </div>
        </div>

        {/* Business Details */}
        <div className="space-y-2 mb-4">
          {business.address && (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <MapPin className="w-4 h-4" />
              <span>{business.address}</span>
            </div>
          )}
          {business.phone && (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Phone className="w-4 h-4" />
              <span>{business.phone}</span>
            </div>
          )}
        </div>

        {/* Description */}
        {business.description && (
          <p className="text-foreground text-sm mb-4 line-clamp-3">
            {business.description}
          </p>
        )}

        {/* Request Form or Button */}
        {showRequestForm ? (
          <div className="space-y-3">
            <Textarea
              value={requestNote}
              onChange={(e) => setRequestNote(e.target.value)}
              placeholder="Add a personal note to your connection request..."
              className="resize-none"
              rows={3}
            />
            <div className="flex gap-2">
              <Button
                onClick={handleSendRequest}
                disabled={isLoading || isOffline}
                size="sm"
                className="flex items-center gap-2"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {isLoading ? 'Sending...' : 'Send Request'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowRequestForm(false)}
                disabled={isLoading}
                size="sm"
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button
            onClick={() => setShowRequestForm(true)}
            disabled={isOffline}
            variant="outline"
            className="w-full flex items-center justify-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            {isOffline ? 'Offline - Cannot Connect' : 'Send Connection Request'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default PartnersBusinessCardShadcn;